import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skull, Mail, Lock, User, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login"
        ? await api.login(email.trim(), password)
        : await api.register(email.trim(), password, name.trim());
      setUser(u);
      toast.success(`Bienvenue, ${u.name || u.email}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 dark" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.12),transparent_55%)]" />
      <Card className="relative w-full max-w-md bg-card/70 border-border p-8 backdrop-blur-xl shadow-red-glow">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
            <Skull className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground">CRIMSON.OPS</div>
            <div className="font-bold text-lg leading-none">{mode === "login" ? "Connexion" : "Créer un compte"}</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">NOM</Label>
              <div className="relative mt-1.5">
                <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Ton nom" className="pl-9 bg-background border-border focus-visible:ring-primary" />
              </div>
            </div>
          )}
          <div>
            <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">{mode === "login" ? "E-MAIL OU IDENTIFIANT" : "E-MAIL"}</Label>
            <div className="relative mt-1.5">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="email-input" type={mode === "login" ? "text" : "email"} required value={email} onChange={e => setEmail(e.target.value)} placeholder={mode === "login" ? "toi@exemple.com ou identifiant" : "toi@exemple.com"} className="pl-9 bg-background border-border focus-visible:ring-primary" />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">MOT DE PASSE</Label>
            <div className="relative mt-1.5">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="password-input" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 bg-background border-border focus-visible:ring-primary" />
            </div>
          </div>
          <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono tracking-widest gap-2">
            <ShieldCheck className="h-4 w-4" /> {busy ? "..." : mode === "login" ? "SE CONNECTER" : "CRÉER LE COMPTE"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-border" /><span className="font-mono text-[10px] text-muted-foreground">OU</span><div className="h-px flex-1 bg-border" />
        </div>

        <Button data-testid="google-login-btn" onClick={google} variant="outline" className="w-full font-mono text-xs gap-2 border-border">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continuer avec Google
        </Button>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "login" ? "Pas de compte ?" : "Déjà inscrit ?"}{" "}
          <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-primary font-semibold hover:underline">
            {mode === "login" ? "Créer un compte" : "Se connecter"}
          </button>
        </div>
      </Card>
    </div>
  );
}
